<template>
  <AppCard
    class="border h-100 d-flex flex-column pa-6"
    elevation="0"
    data-locator="subscription-consumption-dataroom-card"
    :data-id="dataroom.id"
  >
    <!-- Header: name + subscription and renewal dates tooltip -->
    <div class="d-flex align-center mb-1">
      <p
        class="mb-0 text--x-large font-weight-bold text-text-primary text-truncate"
        data-locator="subscription-consumption-dataroom-card-name"
      >
        {{ dataroom.name }}
      </p>
      <AppInfoTooltip data-locator="subscription-consumption-dataroom-card-dates">
        <p class="mb-0 font-weight-bold">
          {{ $t(`${TRANSLATION_KEY_PREFIX}.subscriptionDate`, { date: subscriptionDate }) }}
        </p>
        <p class="mb-0 font-weight-bold">
          {{ $t(`${TRANSLATION_KEY_PREFIX}.renewalDate`, { date: renewalDate }) }}
        </p>
      </AppInfoTooltip>
    </div>

    <!-- Last update + plan -->
    <p class="mb-0 text--small text-text-secondary">
      {{ $t(`${TRANSLATION_KEY_PREFIX}.lastUpdate`, { date: lastUpdateDate, time: lastUpdateTime }) }}
    </p>
    <p class="mb-0 text--small text-text-secondary">
      {{ $t(`${TRANSLATION_KEY_PREFIX}.plan`, { storage: planStorageLabel, duration: dataroom.initialDuration }) }}
    </p>

    <!-- Storage usage -->
    <div class="d-flex align-center justify-space-between mt-5 mb-2">
      <span class="text--medium font-weight-bold text-text-primary">
        {{ $t(`${TRANSLATION_KEY_PREFIX}.storage`) }}
      </span>
      <div class="d-flex align-center column-gap-1 text--small text-text-secondary"
           :class="{ 'text-text-danger': isStorageExceeded }"
      >
        <app-material-icon
          v-if="isStorageExceeded"
          name="emergency_home"
          size="sm"
          color="icon-danger"
        />
        <span>
          {{ dataroom.totalSizeGb }} {{ $t(`${TRANSLATION_KEY_PREFIX}.sizeUnit.Gb`) }}
          <i18n-t :keypath="`${TRANSLATION_KEY_PREFIX}.used`"
                  tag="span"
                  scope="global"
                  :plural="dataroom.totalSizeGb"
          />
          {{ dataroom.currentStorageGb }} {{ $t(`${TRANSLATION_KEY_PREFIX}.sizeUnit.Gb`) }}
        </span>
      </div>
    </div>

    <!-- Storage bar -->
    <SubscriptionDataroomStorageBar
      :segments="storageBarSegments"
      data-locator="subscription-consumption-dataroom-card-progress"
    />

    <!-- Actions -->
    <div class="d-flex flex-wrap column-gap-2 row-gap-2 mt-auto pt-10">
      <app-button
        type="outlined"
        variant="neutral"
        data-locator="subscription-consumption-dataroom-card-share"
        @click="openShareSubscriptionConsumptionDialog"
      >
        <app-material-icon name="mail" size="sm" />
        {{ $t(`${TRANSLATION_KEY_PREFIX}.shareByEmail`) }}
      </app-button>

      <app-button
        type="outlined"
        variant="neutral"
        data-locator="subscription-consumption-dataroom-card-increase-storage"
        @click="$emit('increase-storage', dataroom)"
      >
        <app-material-icon name="arrow_upward" size="sm" />
        {{ $t(`${TRANSLATION_KEY_PREFIX}.increaseStorage`) }}
      </app-button>
    </div>

    <!-- DIALOGS -->
    <ShareSubscriptionConsumptionDialog v-if="shareSubscriptionConsumptionDialogIsOpen"
                                        :title="shareSubscriptionConsumptionDialogTitle"
                                        :submit-action="SHARE_SUBSCRIPTION_DR_PPU"
                                        :action-params="{ roomId: dataroom.id }"
                                        :ok-loading="shareSubscriptionDrPpuPending"
                                        @close="closeShareSubscriptionConsumptionDialog"
    />
  </AppCard>
</template>

<script lang="ts">
  import { defineComponent, type PropType } from 'vue'
  import { mapActions, mapState } from 'vuex'

  import AppCard from '@/common/AppCard.vue'
  import AppInfoTooltip from '@/common/AppInfoTooltip.vue'
  import { getDataroomStorageSize } from '@/common/utils/datarooms'
  import { ISOToDDMMYYYY, ISOToHourMinute, ISOToLongDate } from '@/common/utils/dates'
  import { SHARE_SUBSCRIPTION_DR_PPU } from '@/store/modules/subscription/action_types'
  import ShareSubscriptionConsumptionDialog from '@/subscriptions/views/ShareSubscriptionConsumptionDialog.vue'
  import SubscriptionDataroomStorageBar, { type DataroomStorageBarSegment } from '@/subscriptions/views/SubscriptionDataroomStorageBar.vue'
  import type { SubscriptionDrPpuDataroom } from '@/types/subscription'

  const TRANSLATION_KEY_PREFIX = 'subscriptions.views.SubscriptionConsumption.Datarooms'

  export default defineComponent({
    name: 'SubscriptionConsumptionDrPpuCard',
    components: { AppCard, AppInfoTooltip, ShareSubscriptionConsumptionDialog, SubscriptionDataroomStorageBar },
    props: {
      dataroom: {
        type: Object as PropType<SubscriptionDrPpuDataroom>,
        required: true,
      },
    },
    emits: ['increase-storage'],
    data () {
      return {
        TRANSLATION_KEY_PREFIX,
        shareSubscriptionConsumptionDialogIsOpen: false,
      }
    },
    computed: {
      ...mapState('subscription', ['shareSubscriptionDrPpuPending']),
      shareSubscriptionConsumptionDialogTitle (): string {
        return this.$t('subscriptions.views.SubscriptionConsumption.ShareDialog.title', { itemName: this.dataroom.name })
      },
      lastUpdateDate (): string {
        return ISOToLongDate(this.dataroom.lastUpdateDate)
      },
      lastUpdateTime (): string {
        return ISOToHourMinute(this.dataroom.lastUpdateDate)
      },
      subscriptionDate (): string {
        return ISOToDDMMYYYY(this.dataroom.createdAt)
      },
      renewalDate (): string {
        return ISOToDDMMYYYY(this.dataroom.renewalDate)
      },
      planStorageLabel (): string {
        const size = getDataroomStorageSize(this.dataroom.currentStorageGb)
        const unit = this.$t(`${TRANSLATION_KEY_PREFIX}.sizeUnit.${size.unit}`)
        return `${size.value} ${unit}`
      },
      isStorageExceeded (): boolean {
        return this.dataroom.totalSizeGb > this.dataroom.currentStorageGb
      },
      usedPercent (): number {
        if (!this.dataroom.currentStorageGb) {
          return this.dataroom.totalSizeGb > 0 ? 100 : 0
        }
        return Math.min(100, (this.dataroom.totalSizeGb / this.dataroom.currentStorageGb) * 100)
      },
      storageBarSegments (): DataroomStorageBarSegment[] {
        return [{ percent: this.usedPercent, color: this.isStorageExceeded ? 'surface-danger-bold' : 'pink-400' }]
      },
    },
    methods: {
      ...mapActions('subscription', [SHARE_SUBSCRIPTION_DR_PPU]),
      openShareSubscriptionConsumptionDialog () {
        this.shareSubscriptionConsumptionDialogIsOpen = true
      },
      closeShareSubscriptionConsumptionDialog () {
        this.shareSubscriptionConsumptionDialogIsOpen = false
      },
    },
  })
</script>
